// AuthContext - Gestion de l'authentification
import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { appliquerAccent, appliquerAccentDuTenant } from "@/utils/accentTenant";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/hub`;

const AuthContext = createContext(null);

// Pose FERMEMENT le contexte tenant (current_tenant_slug + current_business_model)
// au LOGIN, avant le 1er rendu, en écrasant tout résiduel d'une session précédente.
//
// Pourquoi : le profil métier (isInstallateurProfile → menus/grilles OMV vs TEF) lit
// `localStorage.current_business_model` de façon SYNCHRONE et NON réactive. OmniHub ne
// l'actualise qu'en ASYNC (après le fetch /user/tenants). Sans pose ferme au login, le
// 1er rendu utilise la valeur résiduelle du compte précédent → fuite de profil d'un
// utilisateur à l'autre (résidu "installateur" → un user TEF voit OMV ; résidu "" →
// Solène voit TEF). On supprime cette fenêtre en résolvant le tenant ICI, puis OmniHub
// et handleSwitchTenant maintiennent EXACTEMENT la même valeur (source unique =
// business_model du tenant résolu) → aucune valeur résiduelle possible.
//
// Règle :
//   - mono-tenant (Solène, Stéphanie, installateurs…) : on pose SON tenant.
//   - super_admin (multi-tenant) : TEF par défaut au login ; le switch via le header
//     (handleSwitchTenant) prend ensuite le relais et fait autorité (il persiste en DB
//     et survit au reload, qui passe par checkAuth — pas par cette fonction).
async function applyLoginTenantContext(loggedUser, sessionToken) {
  const PLATFORM = `${process.env.REACT_APP_BACKEND_URL}/api/platform`;
  const isSuperAdmin =
    loggedUser?.role === "super_admin" || loggedUser?.is_super_admin === true;
  try {
    const res = await axios.get(`${PLATFORM}/user/tenants`, {
      params: { user_email: loggedUser.email },
      withCredentials: true,
      headers: sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {},
    });
    const list = res.data?.tenants || [];
    let target = null;
    if (isSuperAdmin) {
      // super_admin : TEF par défaut (le switch header bascule ensuite).
      target =
        list.find((t) => t.slug === "tef") ||
        list.find((t) => t.is_default) ||
        list[0];
    } else {
      // mono-tenant : son tenant (users.current_tenant), sinon défaut / 1er de SA liste.
      target =
        list.find((t) => t.slug === loggedUser.current_tenant) ||
        list.find((t) => t.is_default) ||
        list[0];
    }
    if (target) {
      localStorage.setItem("current_tenant_slug", target.slug);
      // Même source que handleSwitchTenant → cohérence stricte, 0 résiduel.
      localStorage.setItem("current_business_model", target.business_model || "");
      localStorage.setItem("current_segments", JSON.stringify(target.segments || []));
      appliquerAccentDuTenant(target);
      return;
    }
  } catch (e) {
    // réseau KO → fallback dégradé mais sûr ci-dessous
  }
  // Fallback (fetch indisponible) : poser le slug depuis current_tenant (TEF pour un
  // super_admin) et NEUTRALISER le profil (vide = TEF/résidentiel, jamais installateur
  // par défaut). OmniHub recalculera business_model dès que /user/tenants répond.
  if (isSuperAdmin) {
    localStorage.setItem("current_tenant_slug", "tef");
  } else if (loggedUser?.current_tenant) {
    localStorage.setItem("current_tenant_slug", loggedUser.current_tenant);
  } else {
    localStorage.removeItem("current_tenant_slug");
  }
  localStorage.removeItem("current_business_model");
  localStorage.removeItem("current_segments");
  appliquerAccent(null);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Verifier l'authentification au chargement
  const checkAuth = useCallback(async () => {
    try {
      // Ajouter le token du localStorage si present
      const storedToken = localStorage.getItem('session_token');
      const headers = storedToken ? { Authorization: `Bearer ${storedToken}` } : {};
      
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        headers
      });
      setUser(response.data);
      // Stocker aussi dans localStorage pour les composants qui en ont besoin
      localStorage.setItem('user', JSON.stringify(response.data));
      setError(null);
    } catch (err) {
      setUser(null);
      localStorage.removeItem('session_token');
      // Ne pas considerer 401 comme une erreur
      if (err.response?.status !== 401) {
        console.error("Auth check error:", err);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // ─────────────────────────────────────────────────────────────────────────
  //  SSO Emergent — RETIRÉ le 2026-08-05 (#356), suite du #312
  //
  //  Deux résidus vivaient encore ici après la neutralisation du #312, qui
  //  avait coupé les DEUX moitiés du chemin côté serveur (route
  //  `/auth/session` neutralisée, entrée d'allowlist retirée du middleware) :
  //
  //    · `loginWithGoogle`      redirigeait en dur vers l'hôte SSO d'Emergent
  //                             — le seul appel sortant vers ce tiers qui
  //                             subsistait dans le front ;
  //    · `processOAuthCallback` postait sur `/auth/session` et n'avait PLUS
  //                             AUCUN APPELANT depuis le retrait du `useEffect`
  //                             au #312.
  //
  //  ⚠️ Le bouton Google était `disabled` : le DÉSACTIVER n'est pas le RETIRER.
  //  Un bouton désactivé garde son handler, son URL en dur et son chemin de
  //  code — il suffisait d'ôter un attribut pour le rouvrir. Le bouton, les
  //  deux fonctions et la référence à l'hôte sont supprimés, pas neutralisés.
  //
  //  Ne pas réintroduire de SSO tiers ici sans décision explicite : le seul
  //  chemin d'authentification est `loginWithEmail` → `POST /api/auth/login`.
  // ─────────────────────────────────────────────────────────────────────────

  const [mustChangePassword, setMustChangePassword] = useState(false);

  // Login avec email/mot de passe
  const loginWithEmail = async (email, password) => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      
      // Stocker le token en localStorage comme backup
      if (response.data.session_token) {
        localStorage.setItem('session_token', response.data.session_token);
      }
      
      // Stocker les infos utilisateur pour les composants qui en ont besoin
      if (response.data.user) {
        localStorage.setItem('user', JSON.stringify(response.data.user));

        // Pose FERME du contexte tenant AVANT le rendu (écrase tout résiduel d'une
        // session précédente) : empêche la fuite de profil OMV↔TEF d'un compte à
        // l'autre. Awaité pour que le 1er rendu lise déjà les bonnes valeurs.
        // Cf. applyLoginTenantContext (mono-tenant ferme / super_admin = TEF défaut,
        // switch header autoritaire ensuite).
        await applyLoginTenantContext(response.data.user, response.data.session_token);
      }

      // Vérifier si changement de mdp obligatoire
      if (response.data.must_change_password) {
        setMustChangePassword(true);
        setUser(response.data.user);
        setError(null);
        return { ...response.data.user, must_change_password: true };
      }
      
      setUser(response.data.user);
      setError(null);
      return response.data.user;
    } catch (err) {
      // 🔴 N°498 — UN REFUS DE CONNEXION DOIT NOMMER SON MOTIF.
      // ⚠️ MESURÉ le 20/09 : depuis `visite.omnihub-ia.fr`, le POST est bloqué
      // par CORS AVANT d'atteindre le serveur. `err.response` est alors
      // `undefined` et l'ancien message rendait « Erreur de connexion » — qu'on
      // lit comme un mauvais mot de passe. On cherchait donc le défaut du
      // MAUVAIS côté, alors que le serveur ⛔ n'avait JAMAIS reçu la requête
      // (⛔ aucune trace dans `user_login_history` — vérifié).
      // 🔑 Trois situations, trois phrases : refus métier · erreur HTTP ·
      // serveur injoignable. ⛔ Jamais un message unique qui les confond.
      // ⚠️ `detail` peut être une LISTE (422 Pydantic) : `new Error([{…}])`
      // donnerait `[object Object]` — même famille de défaut, déjà payée.
      const d = err.response?.data?.detail;
      let message;
      if (typeof d === "string" && d.trim()) {
        message = d;
      } else if (Array.isArray(d) && d.length) {
        message = d.map((e) => e?.msg).filter(Boolean).join("\n")
                  || `Erreur ${err.response.status}`;
      } else if (err.response) {
        message = `Erreur ${err.response.status}`;
      } else {
        message = "Serveur injoignable depuis cette adresse — la connexion n'a "
                + "pas pu être tentée. Vos identifiants ne sont pas en cause.";
      }
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  };

  // 🔴 CHANGEMENT DE MOT DE PASSE — L'ANCIEN EST EXIGÉ PAR L'API.
  //
  // ⚠️ CE QUI ÉTAIT CASSÉ, mesuré le 2026-09-17 : cette fonction envoyait
  // `{ new_password }` SEUL, alors que `POST /auth/change-password`
  // (`marketing/routes.py:579`) exige `current_password` depuis le durcissement
  // du 07/09. ⇒ Le serveur répondait « Le mot de passe actuel est requis. » et
  // LE GESTE ÉTAIT STRICTEMENT IMPOSSIBLE.
  // 🔴 CONSÉQUENCE : tout nouvel utilisateur reçoit un mot de passe provisoire,
  // et `must_change_password` bloque TOUTE écriture tant qu'il n'est pas changé.
  // ⇒ ⛔ AUCUN nouveau compte ⛔ ne pouvait RIEN créer. Défaut produit, ⛔ pas un
  // incident de démo.
  // 🔑 LE CORRECTIF EST DANS LE FORMULAIRE, ⛔ PAS DANS L'API : l'exigence du
  // mot de passe actuel est une RÉ-AUTHENTIFICATION délibérée — la retirer
  // rouvrirait la prise de contrôle par session héritée qu'elle a fermée.
  const changePassword = async (newPassword, currentPassword) => {
    try {
      const storedToken = localStorage.getItem('session_token');
      await axios.post(`${API}/auth/change-password`,
        { new_password: newPassword, current_password: currentPassword }, {
        withCredentials: true,
        headers: storedToken ? { Authorization: `Bearer ${storedToken}` } : {}
      });
      setMustChangePassword(false);
      return true;
    } catch (err) {
      throw new Error(err.response?.data?.detail || "Erreur lors du changement de mot de passe");
    }
  };

  // Deconnexion
  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      console.error("Logout error:", err);
    } finally {
      localStorage.removeItem('session_token');
      localStorage.removeItem('user');
      // Purger le contexte tenant : sinon il survit à la déconnexion et fuit sur le
      // login suivant (le compte B hériterait du profil OMV/TEF du compte A).
      // Le prochain login le repose fermement via applyLoginTenantContext.
      localStorage.removeItem('current_tenant_slug');
      localStorage.removeItem('current_business_model');
      localStorage.removeItem('current_segments');
      appliquerAccent(null);
      setUser(null);
    }
  };

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    mustChangePassword,
    loginWithEmail,
    changePassword,
    logout,
    checkAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export default AuthContext;
